import { createMuiTheme, Theme, ThemeOptions } from '@material-ui/core/styles';
import createSpacing from '@material-ui/core/styles/createSpacing';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { Palette } from '@material-ui/core/styles/createPalette';
import { colors, supportColors, buttonColors } from './colors';
import { EventType } from '../types';
import { eventColors } from '../utils/event-type';

interface IPalette extends Palette {
  customColors: {
    darkestBlue: string;
    lightBlue: string;
    navyBlue: string;
    midnightBlue: string;
    lightSteelBlue: string;
    lightestBlue: string;
    powderBlue: string;
    darkestGray: string;
    dimGray: string;
    darkGray: string;
    lightGray: string;
    gainsboro: string;
    white: string;
    pastelPink: string;
    lightestPink: string;
    darkYellow: string;
    lightYellow: string;
    darkGreen: string;
    lightGreen: string;
    darkOrange: string;
    lightOrange: string;
    darkRed: string;
    lightRed: string;
    darkBlue: string;
    skyBlue: string;
    green: string;
  };
}

export type EventTypeColors = Record<EventType | 'default', string>;

export interface ITheme extends Theme {
  palette: IPalette;
  eventColors: EventTypeColors;
}

/* 
interface IThemeOptions extends ThemeOptions {
  palette: IPalette;
} */

const spacing = createSpacing(8);
const breakpoints = createBreakpoints({});

const createCustomTheme = (
  options?: ThemeOptions & {
    eventColors: Record<EventType | 'default', string>;
  },
) => {
  return createMuiTheme({
    eventColors,
    palette: {
      common: colors,
      background: {
        default: colors.white,
      },
      primary: {
        main: colors.darkestBlue,
      },
      secondary: {
        main: colors.white,
      },
      error: {
        main: supportColors.supportColorAlert,
      },
      warning: {
        main: colors.darkestGray,
      },
      info: {
        main: colors.darkestGray,
      },
      success: {
        main: colors.darkestBlue,
      },
      text: {
        primary: colors.darkestGray,
        secondary: colors.white,
      },
    },
    typography: {
      fontFamily: 'DM Sans',
      fontSize: 16,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightBold: 700,
      h1: {
        fontFamily: 'DM Serif Display',
        fontSize: 48,
        fontWeight: 'normal',
        lineHeight: '56px',
      },
      h2: {
        fontFamily: 'DM Serif Display',
        fontSize: 34,
        fontWeight: 'normal',
        lineHeight: '40px',
      },
      h3: {
        fontFamily: 'DM Serif Display',
        fontSize: 24,
        fontWeight: 'normal',
        lineHeight: '32px',
      },
      h4: {
        fontFamily: 'DM Sans',
        fontSize: 20,
        fontWeight: 'bold',
        lineHeight: '26px',
      },
      body1: {
        fontFamily: 'DM Sans',
        fontSize: 16,
        lineHeight: '21px',
      },
      body2: {
        fontSize: 14,
        lineHeight: '18px',
      },
      subtitle1: {
        fontSize: 12,
        lineHeight: '16px',
      },
    },
    shape: {
      borderRadius: spacing(4),
    },
    overrides: {
      MuiGrid: {
        item: {
          padding: 12,
        },
      },
      MuiSnackbar: {
        root: {
          display: 'block',
          height: 53,
          [breakpoints.up('md')]: {
            width: 520,
          },
        },
        anchorOriginTopCenter: {
          height: 53,
          [breakpoints.up('xs')]: {
            top: 80,
            left: '50%',
            right: 'auto',
            transform: 'translateX(-50%)',
          },
          [breakpoints.up('md')]: {
            top: 24,
            left: '50%',
            right: 'auto',
            transform: 'translateX(-50%)',
          },
        },
      },
      MuiSnackbarContent: {
        root: {
          backgroundColor: colors.darkestGray,
          boxShadow:
            '0px 3px 5px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)',
          borderRadius: 4,
          fontSize: 16,
        },
        action: {
          fontSize: 16,
        },
      },
      MuiCssBaseline: {
        '@global': {
          html: {
            height: '100%',
          },
          body: {
            height: '100%',
            fontSize: 16,
            backgroundColor: colors.white,
          },
          '#root': {
            height: '100%',
          },
        },
      },
      MuiLink: {
        root: {
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '16px',
          color: colors.darkestBlue,
          textDecoration: 'none',
          '&:hover': {
            color: '#162D5A',
          },
          '&:focus': {
            color: '#12254A',
          },
          '&$disabled': {
            color: '#7788A7',
          },
        },
        underlineHover: {
          textDecoration: 'none',
          '&:hover': {
            color: '#162D5A',
            textDecoration: 'none',
          },
          '&:focus': {
            color: '#12254A',
            textDecoration: 'none',
          },
        },
      },
      MuiCard: {
        root: {
          padding: 24,
          backgroundColor: colors.white,
          boxShadow: '0px 6px 12px rgb(27 27 28 / 15%)',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(27, 27, 28, 0.15)',
          },
          borderRadius: 16,
          boxSizing: 'border-box',
        },
      },
      MuiPaper: {
        elevation1: {
          boxShadow: 'none',
        },
      },
      MuiCardHeader: {
        root: {
          padding: 0,
        },
      },
      MuiCardContent: {
        root: {
          padding: 0,
          '&:last-child': {
            paddingBottom: 0,
          },
        },
      },
      MuiCardActions: {
        root: {
          padding: 0,
          '&:first-child': {
            marginLeft: 0,
          },
        },
      },
      MuiButton: {
        root: {
          width: '100%',
          paddingTop: spacing(1.5),
          paddingBottom: spacing(1.5),
          fontSize: 16,
          [breakpoints.up('sm')]: {
            fontSize: 18,
          },
          fontWeight: 400,
          lineHeight: 1.5,
          textTransform: 'none',
          letterSpacing: 1,
          textAlign: 'center',
        },
        sizeSmall: {
          paddingTop: spacing(1),
          paddingBottom: spacing(1),
          fontSize: 12,
        },
        label: {
          transition: 'color 250ms',
        },
        disabled: {},
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
          '&:focus': {
            boxShadow: 'none',
          },
        },
        text: {
          letterSpacing: 0,
          fontSize: 16,
          textDecoration: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        containedPrimary: {
          color: buttonColors.primaryButtonFontColorDefault,
          '&$disabled': {
            color: colors.white,
            backgroundColor: buttonColors.primaryButtonBgDisabled,
          },
        },
        containedSecondary: {
          borderStyle: 'solid',
          borderWidth: 2,
          backgroundColor: colors.white,
          borderColor: buttonColors.secondaryButtonDefault,
          color: buttonColors.secondaryButtonDefault,
          '&:hover': {
            backgroundColor: colors.white,
          },
        },
        outlined: {
          boxShadow: 'none',
          padding: spacing(1.3),
          borderColor: colors.darkestBlue,
          borderStyle: 'solid',
          borderWidth: 2,
          color: colors.darkestBlue,
          '&$disabled': {
            color: colors.lightGray,
            border: [[2, 'solid', colors.lightGray]],
          },
          '&:hover': {
            backgroundColor: 'transparent',
            color: colors.darkestBlue,
          },
        },
      },
      MuiIconButton: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        label: {
          fontWeight: 'bold',
          fontSize: 16,
          lineHeight: '21px',
        },
      },
      MuiBackdrop: {
        root: {
          backgroundColor: 'rgba(68, 68, 68, 0.4)',
        },
      },
      MuiDialog: {
        paper: {
          padding: spacing(3),
          boxShadow: '0px 16px 48px rgba(205, 205, 205, 0.5)',
        },
        paperWidthSm: {
          maxWidth: 449,
        },
      },
      MuiDialogTitle: {
        root: {
          padding: 0,
          marginBottom: spacing(2),
          width: '95%',
          '& h2': {
            fontFamily: 'DM Serif Display',
            fontSize: 25,
            lineHeight: 1.28,
            fontStyle: 'normal',
            fontWeight: 'normal',
            color: colors.darkestGray,
          },
        },
      },
      MuiDialogContent: {
        root: {
          padding: 0,
          marginBottom: spacing(4),
        },
      },
      MuiDialogContentText: {
        root: {
          color: colors.darkestGray,
          marginBottom: 0,
        },
      },
      MuiDialogActions: {
        spacing: {
          padding: 0,
        },
        root: {
          flexDirection: 'column',
          justifyContent: 'center',
          width: '100%',
        },
      },
      MuiMenu: {
        paper: {
          width: 250,
          boxShadow:
            '0px 11px 15px rgba(0, 0, 0, 0.2), 0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12)',
          borderRadius: 16,
        },
      },
      MuiMenuItem: {
        root: {
          paddingTop: '16px',
          paddingBottom: '16px',
        },
      },
      MuiSelect: {
        select: {
          padding: '12px 24px 12px 12px',
          '&:focus': {
            backgroundColor: 'none',
          },
        },
      },
      MuiTabs: {
        fixed: {
          borderBottom: colors.lightGray,
          borderBottomStyle: 'solid',
        },
      },
      MuiTab: {
        root: {
          textAlign: 'left',
          textTransform: 'none',
        },
      },
      MuiDrawer: {
        paper: {
          width: '100%',
          background: colors.white,
          borderRadius: '16px 0px 0px 16px',
          [breakpoints.up('sm')]: {
            width: '657px',
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          width: 131,
          height: 34,
          fontSize: 14,
          textAlign: 'center',
          fontWeight: 'bold',
          borderRadius: 4,
          padding: '7px 0',
          color: colors.white,
          backgroundColor: colors.darkestGray,
        },
      },
    },
    ...options,
  });
};

const theme = createCustomTheme();

export default theme;
